<template>
  <div
    ref="container"
    class="saved-view-tabs"
  >
    <m-spinner
      v-if="loading || headerWidth === 0"
      size="xs"
      class="_spinner"
    />
    <template v-else>
      <m-draggable
        v-if="!tight"
        :disabled="!canReorder"
        class="_saved-view-selector"
        draggable-item-class="saved-view-tab"
        ghost-item-class="saved-view-tab"
        dragover-item-class="saved-view-tab"
        can-drag-over-right
        can-drag-over-left
        ghost-item-movement-axes="x"
        :recreate-key="tabs.map(v => v.uid)"
        :drag-between-height="38"
        @set-drag-item="setDragItem"
        @over-right="setOverRight"
        @over-left="setOverLeft"
        @drag-drop="handleDrop"
        @cancel="cancelDragging"
      >
        <saved-view-tab
          v-for="view in sortedViewsToDisplay"
          :key="view.uid"
          :view="view"
          :dragging="dragItemId !== ''"
          :active="view.uid === selectedView.uid"
          :allowed-view-types="allowedViewTypes"
          :view-application="viewApplication"
          :disable-delete="viewsService.views.value.length === 1"
          :read-only="(view.isPublic === true && contextCanEdit === false) || tempViewOnly"
          :dragging-over-left="dragItemId !== '' && draggingOverLeft.includes(view.uid)"
          :dragging-over-right=" dragItemId !== '' && draggingOverRight.includes(view.uid)"
          :show-visibility-hint="showVisibilityHint"
          @select="selectView"
          @delete="deleteView"
          @duplicate="duplicateView"
          @open-editor="handleOpenEditor(view)"
        />
      </m-draggable>
      <m-dropdown
        v-if="tabsAreWrapped || tight || showViewList"
        v-model:value="showViewList"
        :title="$t('savedViewSelector.selectView')"
        :disabled="!shouldShowViewList"
      >
        <m-btn
          id="more"
          data-cy="more-views"
          hide-border
          small
          :light="!tight"
          class="_btn"
          :clickable="shouldShowViewList"
          @click="showViewList = true"
        >
          <div
            v-if="!tight"
            class="_inner"
          >
            {{ unshownTabNumber }} {{ $t('savedViewSelector.more') }}
          </div>
          <div
            v-else
            class="_selector"
          >
            <saved-view-title
              :view="selectedView"
              :show-visibility-hint="showVisibilityHint"
            />
            <m-icon
              v-if="shouldShowViewList"
              type="down"
              size="11"
              :color="$colors.grey.lighten1"
              :style="{ 'margin-left': '0.6rem'}"
            />
          </div>
        </m-btn>
        <template #overlay>
          <m-card
            list
            no-padding
          >
            <m-draggable
              :disabled="!canReorder"
              class="_more-saved-view-item-container"
              draggable-item-class="_handle"
              ghost-item-class="m-card-item"
              dragover-item-class="m-card-item"
              can-drag-over-top
              can-drag-over-bottom
              :recreate-key="tabOrder"
              :drag-between-height="24"
              @set-drag-item="setDragItem"
              @over-top="setOverTop"
              @over-bottom="setOverBottom"
              @drag-drop="handleDrop"
              @cancel="cancelDragging"
            >
              <div
                v-for="view in viewsService.views.value"
                :key="view.uid"
                class="_saved-view-item"
              >
                <div
                  v-if="draggingOverTop.includes(view.uid)"
                  class="_drag-over-top"
                />
                <saved-view-item
                  :view="view"
                  :show-drag-handle="canReorder"
                  :active="view.uid === selectedView.uid"
                  :allowed-view-types="allowedViewTypes"
                  :view-application="viewApplication"
                  :disable-delete="viewsService.views.value.length === 1"
                  :show-visibility-hint="showVisibilityHint"
                  @select="selectView"
                  @delete="deleteView"
                  @duplicate="duplicateView"
                  @open-editor="handleOpenEditor"
                />
                <div
                  v-if="draggingOverBottom.includes(view.uid)"
                  class="_drag-over-bottom"
                />
              </div>
            </m-draggable>
            <template
              v-if="showCreateBtn"
            >
              <m-divider xxs />
              <m-card-item
                icon="plus"
                :loading="createLoading"
                :disabled="addViewLoading"
                @click="addView"
              >
                {{ $t('savedViewSelector.addView') }}
              </m-card-item>
            </template>
          </m-card>
        </template>
      </m-dropdown>
      <div
        v-else-if="showCreateBtn && !isPrint"
        id="addBtn"
        ref="addBtn"
      >
        <m-btn
          icon="plus"
          data-cy="create-view"
          hide-border
          small
          light
          fab
          :loading="createLoading"
          :disabled="addViewLoading"
          @click="addView"
        />
      </div>
    </template>
    <template
      v-if="viewsService.currentViewHasUnsavedChanges.value && !viewsService.context.readOnly.value && !$store.state.breakpoint.smAndDown"
    >
      <save-view-actions
        ref="saveViewActionsRef"
        :views-service="viewsService"
      />
    </template>
  </div>
</template>

<script setup>
import SaveViewActions from '@/components/list/SaveViewActions.vue';
import SavedViewItem from '@/components/list/SavedViewItem.vue';
import SavedViewTab from '@/components/list/SavedViewTab.vue';
import SavedViewTitle from '@/components/list/SavedViewTitle.vue';
import useConfirmDialog from '@/composables/confirm-dialog';
import useDebounce from 'shared/composables/debounce';
import usePrintMode from '@/composables/export/print-mode';
import useSnackbar from '@/composables/snackbar';
import useSort from '@/composables/draggable/sort';
import useSortViews from '@/composables/saved-views/view-collections';
import { VIEWS_SERVICE } from '@/lib/constants';
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';
import { generateDuplicateName } from '@/lib/saved-view/saved-view';
import { logCatch } from '@/lib/logger/logger';
import { shallowCopy } from 'shared/lib/copy';
import { sortByArray } from 'shared/lib/sort';
import { uniq } from 'lodash-es';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  showCreate: {
    type: Boolean,
    default: false,
  },
  allowedViewTypes: {
    type: Array,
    default: () => [],
  },
  viewApplication: {
    type: String,
    default: '',
  },
  propertyOption: {
    type: Object,
    default: () => null,
  },
  planning: {
    type: Object,
    default: () => null,
  },
  sharedViewOrder: {
    type: Boolean,
    required: true,
  },
  rightElementWidth: {
    type: Number,
    default: 0,
  },
  headerWidth: {
    type: Number,
    default: 0,
  },
  headerTight: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const viewsService = inject(VIEWS_SERVICE);
const views = ref(viewsService.views);
const selectedView = ref(viewsService.currentView);

const createLoading = ref(false);
const addViewLoading = computed(() => createLoading.value || viewsService.createLoading.value || viewsService.loadingUpdateView.value);

const tight = computed(() => props.headerWidth < 800);

const emit = defineEmits(['tabs-shown-updated', 'open-editor']);

const maxShowTabIndex = ref(1);
const showViewList = ref(false);

const loading = ref(true);

const sortViews = useSortViews();

const { canEdit: contextCanEdit } = viewsService.context;

const showCreateBtn = computed(() => props.showCreate
  && contextCanEdit.value);
const shouldShowViewList = computed(() => showCreateBtn.value === true || viewsService.views.value.length > 1);

const sortedViewsToDisplay = computed(() => tabs.value.slice(0, maxShowTabIndex.value));
const tabsAreWrapped = computed(() => views.value.length > maxShowTabIndex.value);
const unshownTabNumber = computed(() => views.value.length - maxShowTabIndex.value);

const { isPrint } = usePrintMode();

const snackbar = useSnackbar();

const canReorder = computed(() => contextCanEdit.value === true && viewsService.views.value.length > 1);
const {
  setDragItem,
  setOverLeft,
  setOverRight,
  setOverBottom,
  setOverTop,
  draggingOverRight,
  draggingOverLeft,
  draggingOverTop,
  draggingOverBottom,
  dragItemId,
  dropItem,
  cancelDragging,
} = useSort();

const init = () => {
  if (viewsService.viewCollection.value !== null || !showCreateBtn.value) {
    return new Promise((resolve) => {
      resolve();
    });
  }

  return mutateViewOrder(views.value.filter((v) => v.uid !== 0).map((view) => ({ uid: view.uid })))
    .catch(() => {
      // When two clients try to init the same view collection at the same time, then
      // there can be a validation error which is okay.
    });
};

const tabs = computed(() => {
  const viewsCopy = shallowCopy(viewsService.views.value);

  const order = shallowCopy(viewsCopy.map((v) => ({ uid: v.uid })));
  let index = order.findIndex((item) => item.uid === selectedView.value.uid);
  if (index === -1) {
    order.push({ uid: selectedView.value.uid });
    index = order.length - 1;
  }

  if (index >= maxShowTabIndex.value) {
    const insertIndex = Math.min(maxShowTabIndex.value - 1, order.length);
    order.splice(index, 1);
    order.splice(insertIndex, 0, { uid: selectedView.value.uid });
  }

  viewsCopy.sort(sortByArray(order));
  return viewsCopy;
});

const tabOrder = computed(() => {
  if (viewsService.viewCollection.value === null) {
    return [];
  }
  return viewsService.viewCollection.value.viewOrder;
});

const deleteViewFromOrder = (view) => {
  mutateViewOrder(tabOrder.value.filter((v) => v.uid !== view.uid));
};

const mutateViewOrder = (viewOrder) => {
  if (viewsService.viewCollection.value !== null) {
    return sortViews.updateViewCollection({ uid: viewsService.viewCollection.value.uid, viewOrder });
  }

  return sortViews.createViewCollection({ ...selectedView.value, viewOrder }, { shared: props.sharedViewOrder });
};

const handleDrop = () => {
  // This will lead to problems when users have different private views. However,
  // we discussed that and agreed that we accept this for now and might handle
  // that better in the future by splitting up the orders in two: public and private
  const newOrder = dropItem(viewsService.views.value.map((v) => ({ uid: v.uid })));
  mutateViewOrder(newOrder);
};

const distanceToRight = 75;

const calculateMaxTabIndex = () => {
  if (container.value === null) {
    return 1;
  }

  if (tight.value || props.headerTight) {
    return 1;
  }

  let totalWidth = 0;
  const moreBtn = container.value.querySelector('#more');
  if (moreBtn !== null) {
    totalWidth += moreBtn.clientWidth;
  }

  const addBtn = container.value.querySelector('#addBtn');
  if (addBtn !== null) {
    totalWidth += addBtn.clientWidth;
  }

  if (saveViewActionsRef.value !== null) {
    totalWidth += saveViewActionsRef.value.$el.clientWidth;
  }

  let tabIndex = 0;
  const tabs = Array.from(container.value.getElementsByClassName('saved-view-tab'));
  if (tabs.length === 0) {
    return maxShowTabIndex.value;
  }

  // The selected view is always shown, so it has to be considered independent of other tabs
  tabs.sort((a, b) => {
    if (a.dataset.id === `${selectedView.value.uid}`) {
      return -1;
    }
    if (b.dataset.id === `${selectedView.value.uid}`) {
      return 1;
    }
    return 0;
  });
  for (let i = 0; i < tabs.length; i++) {
    totalWidth += tabs[i].clientWidth;
    if (totalWidth < (props.headerWidth - props.rightElementWidth - distanceToRight)) {
      tabIndex += 1;
    }
  }
  return Math.max(1, tabIndex);
};

const reOrderTabs = () => {
  if (views.value.length === 1) {
    maxShowTabIndex.value = 1;
    return;
  }
  maxShowTabIndex.value = views.value.length;
  nextTick(() => {
    maxShowTabIndex.value = calculateMaxTabIndex();
    emit('tabs-shown-updated', maxShowTabIndex.value);
  });
};

const addView = () => {
  const toCreate = [];
  if (views.value[0].isTemp) {
    toCreate.push(views.value[0]);
  }
  toCreate.push({
    title: '',
    emoji: '',
    viewType: props.allowedViewTypes[0],
    viewApplication: props.viewApplication,
    goalSort: [],
    params: viewsService.defaultView.params,
  });

  return createView(toCreate);
};

const createView = (toCreate) => {
  createLoading.value = true;
  viewsService.createViews(toCreate, viewsService.viewCollection.value).then((views) => {
    reOrderTabs();
    if (!tight.value) {
      showViewList.value = false;
    }
    if (views.length === 2) {
      let regularView = views.find((v) => v.title === '');
      if (regularView === undefined) {
        regularView = views[views.length - 1];
      }

      viewsService.changeSelectedView(regularView).then(() => emit('open-editor'));
      return;
    }
    viewsService.changeSelectedView(views[0]).then(() => emit('open-editor'));
  }).catch(logCatch(() => {
    snackbar.error(t('error.default'));
  })).finally(() => {
    createLoading.value = false;
  });
};

const showVisibilityHint = computed(() => uniq(viewsService.views.value.map((v) => v.isPublic)).length > 1);

const tempViewOnly = computed(() => showCreateBtn.value === false && views.value.length === 1 && viewsService.currentView.value.isTemp);

watch(viewsService.currentViewHasUnsavedChanges, () => {
  reOrderTabs();
});

const confirmDialog = useConfirmDialog();

const duplicateView = async (view) => {
  const duplicatedTitle = generateDuplicateName(view.title ?? t('general.untitled'));

  const duplicatedView = {
    ...view,
    title: duplicatedTitle,
    isPublished: false,
  };

  const toCreate = [];
  if (view.isTemp) {
    toCreate.push(view);
  }
  toCreate.push(duplicatedView);
  createView(toCreate);
};

const deleteView = (view) => {
  const deleteMethod = () => {
    viewsService.deleteView(view).then(() => {
      viewsService.changeSelectedView(viewsService.views.value[0]);
      deleteViewFromOrder(view);
      reOrderTabs();
      snackbar.success(t('success.deleted'));
    }).catch(logCatch(() => {
      snackbar.error(t('error.default'));
    }));
  };
  confirmDialog.confirm({
    title: t('general.deletePrompt'),
    okText: t('general.yesDelete'),
    okType: 'danger',
    maskClosable: true,
    cancelText: t('general.cancel'),
    onOk() {
      deleteMethod();
    },
  });
};

const handleOpenEditor = (view) => {
  selectView(view);
  emit('open-editor');
};

defineExpose({ reOrderTabs });

const { debounce } = useDebounce();

const selectView = (view) => {
  viewsService.changeSelectedView(view);
  reOrderTabs();
  showViewList.value = false;
};

watch(() => props.headerWidth, () => {
  debounce(reOrderTabs, 100);
});

watch(tabOrder, () => {
  debounce(reOrderTabs, 100);
});

const container = ref(null);
const saveViewActionsRef = ref(null);
onMounted(() => {
  init().then(() => {
    reOrderTabs();
    loading.value = false;
  });
});
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>

.saved-view-tabs {
  display: flex;
  align-items: center;

  ._spinner {
    height: 3.2rem;
  }

  ._saved-view-selector {
    display: flex;
    align-items: center;

    ._btn {
      ._inner {
        display: flex;
        align-items: center;

        ._text {
          max-width: 15rem;
          margin-right: .4rem;
          margin-left: .6rem;
          font-weight: $font-weight-semibold;
        }
      }
    }
  }

  ._selector {
    display: flex;
  }

  .save-view-btn {
    display: flex;
  }

  ._save-btn {
    margin-left: .8rem;
  }

  ._reset-btn {
    margin-left: .8rem;
  }

  ._item {
    position: relative;

    ._item-inner {
      border-radius: $btn-border-radius;
    }

    ._handle {
      position: absolute;
      top: 50%;
      left: 1.8rem;
      cursor: grab;
      transform: translate(-50%, -50%);
    }

    ._label {
      margin-left: 1.2rem;
    }

    ._right {
      ._tag {
        margin-right: 1.2rem;
      }
    }
  }
}

._saved-view-item {
  position: relative;

  ._drag-over-top {
    position: absolute;
    top: -2px;
    right: 0;
    left: 0;
    z-index: 88;
    width: 100%;
    height: 4px;
    pointer-events: none;
    background: $highlighted-color-dark;
    opacity: 1;
  }

  ._drag-over-bottom {
    position: absolute;
    right: 0;
    bottom: -2px;
    left: 0;
    z-index: 88;
    width: 100%;
    height: 4px;
    pointer-events: none;
    background: $highlighted-color-dark;
    opacity: 1;
  }
}

  ._more-saved-view-item-container {
    max-height: calc(var(--viewport-height-100) - 14rem);
    overflow: auto;
  }
</style>
